import {
  Box,
  TextField as MuiInput,
  TextFieldProps as MuiTextFieldProps,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import { FieldErrors } from "react-hook-form";

import style from "./input.module.css";

interface InputProps extends Omit<MuiTextFieldProps, "error"> {
  register: any;
  label?: string;
  error?: string;
  errors?: FieldErrors;
}

const Input = (props: InputProps) => {
  const {
    register,
    label,
    errors = {},
    placeholder,
    name,
    required,
    // Pull `error` out of the spread props. It is a string, but MUI's TextField
    // `error` prop expects a boolean; if left in `restProps` it would re-spread
    // over the coerced `error={Boolean(error)}` below and trip a prop-type warning.
    error: errorProp,
    InputProps: inputProps,
    ...restProps
  } = props;

  const error = errorProp || (errors[name ?? ""]?.message as string);

  // `disableUnderline` is only meaningful for the standard/filled MUI input variants.
  // Passing it to the outlined variant causes React to forward the prop to the DOM.
  const variant = (restProps as { variant?: MuiTextFieldProps["variant"] }).variant;
  const supportsDisableUnderline = variant === "standard" || variant === "filled";

  return (
    <Box sx={{ flex: 1, flexDirection: "column" }}>
      {label && (
        <Typography
          display="block"
          className={clsx(style.inputLabel, required ? style.required : null)}
        >
          {label}
        </Typography>
      )}
      <MuiInput
        sx={{ display: "flex" }}
        placeholder={placeholder}
        type={name}
        {...register(name)}
        error={Boolean(error)}
        helperText={error}
        FormHelperTextProps={{ sx: { ml: 0 } }}
        // Master-data fields are named generically (`name`, `code`, `email`), which
        // browsers match against saved address/contact profiles and offer unrelated
        // suggestions for. This component is only used on admin data-entry forms —
        // credential fields use CustomInput — so autofill is off by default here.
        // Callers can still opt in by passing an explicit `autoComplete` prop.
        autoComplete="off"
        {...restProps}
        InputProps={{
          ...inputProps,
          ...(supportsDisableUnderline ? { disableUnderline: true } : {}),
          sx: { backgroundColor: "primary.light", ...(inputProps?.sx ?? {}) },
        }}
      />
    </Box>
  );
};
export default Input;
